/**
 * The uncommitted times a drag proposed for an event, handed to whichever
 * surface opens next.
 *
 * Drag-to-reschedule's "Continue editing" reverts the visual move and opens the
 * event's detail surface seeded with the dragged-to times — times the user has
 * not saved. The canvas addresses a card by URN alone (`urn:bb:source:<id>` in a
 * `string[]` stack), so there is nowhere in that address to carry them, and they
 * must not ride the URL either: a shared or reloaded link would then re-propose
 * a move the user never made.
 *
 * So the proposal is handed over out of band, in the same shape as the
 * optimistic overlay next door (see pendingBufferMoves): the drag records it,
 * the detail surface claims it as it mounts, and claiming clears it. One slot —
 * only one drag can be in flight, and a proposal that is never claimed is
 * replaced by the next one rather than accumulating.
 */

export type EventProposal = {
  /** The source ent id (FullCalendar's `gcalId`) the proposal belongs to. */
  eventId: string;
  /** Dragged-to start, ISO. */
  startIso: string;
  /**
   * Dragged-to end, ISO. All-day ends are *exclusive* (midnight after the last
   * day), matching both FullCalendar and the stored event, so a consumer
   * converts to an inclusive end day exactly as it would for a saved event.
   */
  endIso: string;
};

/**
 * How long an unclaimed proposal survives.
 *
 * Deliberately short. The claiming surface is addressed by the event alone, so
 * the card a hand-off opened and a card the user opened themselves are the same
 * address — nothing distinguishes them, and a proposal still in the slot when
 * the second one mounts would seed it with times the user walked away from. The
 * window is the whole exposure, so it is sized to the one thing that legitimately
 * delays a claim — the source fetch behind the card — and nothing else. Seconds,
 * not tens of seconds: long enough that a slow fetch still gets its proposal,
 * short enough that "gave up and reopened it" cannot land inside it.
 */
export const EVENT_PROPOSAL_TTL_MS = 5_000;

let proposal: (EventProposal & { expiresAt: number }) | null = null;

/** Record the times a drag proposed, replacing any unclaimed proposal. */
export const setEventProposal = (next: EventProposal, now: number = Date.now()): void => {
  proposal = { ...next, expiresAt: now + EVENT_PROPOSAL_TTL_MS };
};

/**
 * Take the pending proposal for `eventId`, or null when there is none for it.
 *
 * Claiming clears the slot, so a surface that remounts (an external reschedule
 * remounts the panel by key) re-seeds from the saved event rather than silently
 * re-applying a proposal the user already resolved.
 *
 * An expired proposal is dropped whoever asks for it. Nothing guarantees the
 * surface a hand-off aimed at ever mounts — the source can fail to resolve, or
 * the navigation can be superseded — and without the expiry that abandoned slot
 * would sit there until the next drag, seeding an ordinary open of the same
 * event with a move the user walked away from.
 */
export const claimEventProposal = (
  eventId: string,
  now: number = Date.now(),
): EventProposal | null => {
  if (!proposal) return null;
  if (proposal.expiresAt <= now) {
    proposal = null;
    return null;
  }
  if (proposal.eventId !== eventId) return null;
  const claimed: EventProposal = {
    eventId: proposal.eventId,
    startIso: proposal.startIso,
    endIso: proposal.endIso,
  };
  proposal = null;
  return claimed;
};

/**
 * Drop the proposal for `eventId` without consuming it as a claim.
 *
 * For a surface that has discovered it can never present the hand-off's target —
 * the source failed to resolve, so no card will mount to claim it. Dropping it
 * on the spot beats waiting out the expiry, during which a card the user opens
 * themselves would seed from it.
 */
export const discardEventProposal = (eventId: string): void => {
  if (proposal?.eventId === eventId) proposal = null;
};

/** Drop any pending proposal. Exported for tests and teardown. */
export const clearEventProposal = (): void => {
  proposal = null;
};
